import { IPublicClientApplication } from '@azure/msal-browser';
import { Nullable } from '@customTypes';
import { PARTICIPANT_TOKEN_PARAM } from '@constants';

import { LanguageLocale, Theme } from './app-bar-state';

interface AppBarElement extends HTMLElement {
  config?: AppBarConfig;
  msalInstance?: IPublicClientApplication;
  getAccessToken: () => Promise<AccessToken>;
  setLanguage: (lang: LanguageLocale) => void;
  setTheme: (theme: Theme) => void;
}

interface AccessToken {
  idToken: string;
}

interface AppBarConfig {
  b2c: {
    tenant: string | undefined;
    policies_names_signup_signin: string | undefined;
    client_id: string | undefined;
    scope: string | undefined;
  };
  holos_url_api: string | undefined;
  mkp_url: string | undefined;
  bot: {
    directline_token: string | undefined;
  };
  mode: 'production' | 'development';
}

export class AppBarLoader {
  constructor() {
    const urlParams = new URLSearchParams(window.location.search);
    const hasToken = urlParams.has(PARTICIPANT_TOKEN_PARAM);

    if (hasToken) {
      return;
    }

    const appbar_config: AppBarConfig = {
      b2c: {
        tenant: process.env.REACT_APP_B2C_TENANT,
        policies_names_signup_signin: process.env.REACT_APP_B2C_POLICIES_NAMES_SIGNUP_SIGNIN,
        client_id: process.env.REACT_APP_B2C_CLIENTID,
        scope: process.env.REACT_APP_B2C_SCOPE?.split('/').pop(),
      },
      holos_url_api: process.env.REACT_APP_URL_API,
      mkp_url: process.env.REACT_APP_URL,
      bot: {
        directline_token: process.env.REACT_APP_WEBCHAT_DIRECTLINE_TOKEN,
      },
      mode: 'production',
    };

    const appBarScript = document.querySelector('script[src="libs/app-bar-stencil/app-bar-stencil.esm.js"]');

    if (appBarScript) {
      return;
    }

    (function () {
      const appBarScript = document.createElement('script');

      appBarScript.setAttribute('src', 'libs/app-bar-stencil/app-bar-stencil.esm.js');

      appBarScript.setAttribute('type', 'module');

      appBarScript.onload = () => {
        const customElement = document.querySelector('app-bar');
        // setting up app-bar
        (customElement as AppBarElement).config = appbar_config;
      };

      document.body.appendChild(appBarScript);
    })();
  }

  public async getMsalInstance(timeout = 5000): Promise<IPublicClientApplication | undefined> {
    await customElements.whenDefined('app-bar');
    const appBarElement: AppBarElement | null = document.querySelector('app-bar');

    for (let i = 0; i < 10; i++) {
      if (appBarElement && 'msalInstance' in appBarElement && appBarElement.msalInstance) {
        break;
      }

      await this.delay(timeout / 10);
    }

    return (appBarElement as AppBarElement).msalInstance;
  }

  public async getAccessToken(timeout = 5000): Promise<AccessToken | null> {
    await this.getMsalInstance();
    const appBarElement: AppBarElement | null = document.querySelector('app-bar');

    for (let i = 0; i < 10; i++) {
      if (appBarElement && 'getAccessToken' in appBarElement) {
        const result = await appBarElement.getAccessToken();

        if (result !== null) {
          return result;
        }
      }

      await this.delay(timeout / 10);
    }

    return null;
  }

  private delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  public setLanguage(languageLocale: LanguageLocale) {
    const appBarElement: Nullable<AppBarElement> = document.querySelector('app-bar');

    if (appBarElement) {
      appBarElement.setLanguage(languageLocale);
    }
  }

  public setTheme(theme: Theme) {
    const appBarElement: Nullable<AppBarElement> = document.querySelector('app-bar');

    if (appBarElement) {
      appBarElement.setTheme(theme);
    }
  }
}
